import { JSX } from 'react';
import { i18n, i18nDynamic } from 'shared/i18n';

/**
 * Hand-written stand-ins for the real Slate chrome, kept in one place so the Design
 * Configurator accordions and the Välj Tema preview cannot drift apart.
 *
 * The markup mirrors what the editor actually renders — `Area.js` for the header/footer
 * subareas and `TableOfContent.js` for the TOC — because the theme's SCSS targets those
 * exact selectors. Body text is plain h1–h6 / p, which is what `StyledElement.js` emits.
 */

export interface AreaConfig {
  type?: 'simple' | 'headings' | 'combined';
  options?: {
    pageNumber?: boolean;
    metadataText?: string[];
  };
}

export interface AreaContent {
  texts?: Record<string, string>;
  fallbackKeys?: string[];
  headings?: Array<{ uuid?: string; name?: string }>;
}

export interface PreviewHeader {
  name: string;
  pageNumber?: number;
  children?: PreviewHeader[];
}

export const HIGHLIGHT_CSS = `
  .dc-hover-subarea-headers .subarea-headers,
  .dc-hover-subarea-pagenumber .subarea-pagenumber,
  .dc-hover-subarea-texts .subarea-texts,
  .dc-hover-editor-table-of-content--content .editor-table-of-content--content,
  .dc-hover-toc-item .toc-item,
  .dc-hover-toc-header .toc-header,
  .dc-hover-toc-pagenumber .toc-pagenumber,
  .dc-hover-tag-ol ol,
  .dc-hover-tag-li li,
  .dc-hover-tag-a a {
    outline: 2px solid #547ba2;
    outline-offset: 1px;
  }
`;

export const SAMPLE_HEADINGS: PreviewHeader[] = [
  { name: 'dc.preview.toc-sample-1', pageNumber: 1 },
  {
    name: 'dc.preview.toc-sample-2',
    pageNumber: 3,
    children: [
      { name: 'dc.preview.toc-sample-2a', pageNumber: 4 },
      { name: 'dc.preview.toc-sample-2b', pageNumber: 5 },
    ],
  },
  { name: 'dc.preview.toc-sample-3', pageNumber: 7 },
];

export const SAMPLE_HEADER: AreaConfig = { type: 'headings', options: {} };
export const SAMPLE_FOOTER: AreaConfig = { type: 'simple', options: { pageNumber: true, metadataText: [] } };

function renderHeadings(content: AreaContent) {
  const items =
    content.headings && content.headings.length > 0
      ? content.headings
      : [{ name: i18n('dc:preview.sample-heading-1') }, { name: i18n('dc:preview.sample-heading-2') }];
  return (
    <div className="subarea-headers">
      <ol role="menu" aria-orientation="horizontal">
        {items.map((header, i) => (
          <li key={header.uuid ?? i} role="presentation">
            <a role="menuitem" href="#">
              {header.name || ''}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}

function renderPageNumber(opts: NonNullable<AreaConfig['options']>) {
  return opts.pageNumber ? <div className="subarea-pagenumber">1</div> : null;
}

function renderTexts(opts: NonNullable<AreaConfig['options']>, content: AreaContent) {
  const textKeys = opts.metadataText ?? [];
  return textKeys.length > 0 ? (
    <div className="subarea-texts">
      {textKeys.map((key) => (
        <div key={key} className={`subarea-text-${key}`}>
          {content.texts?.[key] || key}
        </div>
      ))}
    </div>
  ) : null;
}

export function renderArea(area: 'header' | 'footer', config: AreaConfig | undefined, content: AreaContent, hoverModifier: string) {
  const wrapperClass = `area-${area} ${hoverModifier}`.trim();
  if (!config?.type) return <div className={wrapperClass} />;

  if (config.type === 'headings') {
    return <div className={wrapperClass}>{renderHeadings(content)}</div>;
  }

  const opts = config.options ?? {};

  if (config.type === 'combined') {
    return (
      <div className={wrapperClass}>
        {renderHeadings(content)}
        {renderTexts(opts, content)}
        {renderPageNumber(opts)}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {renderPageNumber(opts)}
      {renderTexts(opts, content)}
    </div>
  );
}

export function renderTocEntry(entry: PreviewHeader, key: number, translateName: boolean): JSX.Element {
  const label = translateName ? i18nDynamic(entry.name) : entry.name;
  return (
    <li key={key}>
      <div className="toc-item">
        <a>
          <span className="toc-header">{label}</span>
          {entry.pageNumber != null && <span className="toc-pagenumber">{entry.pageNumber}</span>}
        </a>
      </div>
      {entry.children && entry.children.length > 0 && <ol>{entry.children.map((c, i) => renderTocEntry(c, i, translateName))}</ol>}
    </li>
  );
}

export function renderToc(entries: PreviewHeader[], translateNames: boolean, hoverModifier = '') {
  return (
    <div className={`editor-table-of-content--container ${hoverModifier}`.trim()}>
      <div className="editor-table-of-content--content">
        <ol>{entries.map((entry, i) => renderTocEntry(entry, i, translateNames))}</ol>
      </div>
    </div>
  );
}

export function renderPreviewSection(labelKey: string, children: JSX.Element | JSX.Element[]) {
  return (
    <div className="dc-preview-section" key={labelKey}>
      <div className="dc-preview-section__title">{i18nDynamic(labelKey)}</div>
      <div className="page-content">
        <div className="wrapper">{children}</div>
      </div>
    </div>
  );
}

export function renderSampleBody() {
  return (
    <>
      <h1>{i18n('dc:preview.body-h1')}</h1>
      <p>{i18n('dc:preview.body-text')}</p>
      <h2>{i18n('dc:preview.body-h2')}</h2>
      <p>
        {i18n('dc:preview.body-text')} <a href="#">{i18n('dc:preview.body-link')}</a>.
      </p>
      <h3>{i18n('dc:preview.body-h3')}</h3>
      <p>{i18n('dc:preview.body-text')}</p>
    </>
  );
}

export function renderSampleTable() {
  return (
    <table>
      <thead>
        <tr>
          <th>{i18n('dc:preview.table-col-item')}</th>
          <th>{i18n('dc:preview.table-col-current')}</th>
          <th>{i18n('dc:preview.table-col-previous')}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{i18n('dc:preview.table-row')}</td>
          <td>1 234</td>
          <td>1 100</td>
        </tr>
        <tr>
          <td>{i18n('dc:preview.table-row')}</td>
          <td>567</td>
          <td>540</td>
        </tr>
      </tbody>
    </table>
  );
}

/** Both kinds: some themes replace the bullet with a `li::before`, which an `ol` would not show. */
export function renderSampleLists() {
  return (
    <>
      <ul>
        <li>{i18n('dc:preview.list-item')}</li>
        <li>{i18n('dc:preview.list-item')}</li>
      </ul>
      <ol>
        <li>{i18n('dc:preview.list-item')}</li>
        <li>{i18n('dc:preview.list-item')}</li>
      </ol>
    </>
  );
}

export const PREVIEW_FLOW_CSS = `
  .theme-preview-page .page-content {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    overflow: visible;
  }
  .theme-preview-page .page-content .wrapper {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    /* the page's own margins are dead space in a panel this size */
    padding: 8px 10px;
  }
  .theme-preview-page .page-content .area-header,
  .theme-preview-page .page-content .area-footer {
    position: static;
  }
  /* Room for list markers: they render outside the list's content box and would otherwise hang
     past the panel edge. Not list-style-position:inside — that puts the marker in the line box,
     and the TOC's block-level .toc-item then wraps below its own number. */
  .theme-preview-page .page-content ol,
  .theme-preview-page .page-content ul {
    padding-left: 1.6em;
  }
  .theme-preview-page .page-content table {
    width: 100%;
  }
`;

export const PREVIEW_CHROME_CSS = `
  .theme-preview-page {
    display: block;
    /* The scroll container clips at its edge, and the first title would otherwise sit flush against
       it with its capitals shaved off. */
    padding-top: 4px;
  }
  .dc-preview-section {
    margin: 0 0 14px;
  }
  .dc-preview-section:last-child {
    margin-bottom: 0;
  }
  .dc-preview-section__title {
    /* 1.6 rather than 1.4: at 10px uppercase the tighter line box shaves diacritics like the ring
       on the Å in INNEHÅLLSFÖRTECKNING. */
    font: 600 10px/1.6 system-ui, -apple-system, "Segoe UI", sans-serif;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8a97a3;
    margin: 0 0 4px;
  }
  .dc-preview-section > .page-content {
    border: 1px solid #e3e8ee;
    border-radius: 3px;
  }
`;
